import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CountryRegionData } from 'react-country-region-selector'
import { API_ENDPOINTS } from '../config/api'

const raw = (((CountryRegionData as any).default || CountryRegionData) as string[][])
const COUNTRY_NAMES: string[] = raw.map((c) => c[0])

function nameForCode(code: string): string {
  const e = raw.find((c) => c[1] === code)
  return e ? e[0] : ''
}
function regionsFor(country: string): string[] {
  const e = raw.find((c) => c[0] === country)
  if (!e || !e[2]) return []
  return e[2].split('|').map((r) => r.split('~')[0])
}

export interface ProfileValue {
  country: string
  region: string
}

interface Props {
  onComplete: (profile: ProfileValue) => void
  title?: string
  requireRegion?: boolean
}

/**
 * Request 1, Option B: capture location with near-zero friction.
 * Auto-detect the country/region from the caller's IP, pre-fill it, and let the
 * respondent confirm or correct in one tap. Decoupled from the survey questions.
 */
const ProfileStep: React.FC<Props> = ({ onComplete, title, requireRegion = false }) => {
  const intl = useIntl()
  const [country, setCountry] = useState('')
  const [region, setRegion] = useState('')
  const [detected, setDetected] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch(API_ENDPOINTS.geo.detect)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return
        if (d.country_code) {
          const name = nameForCode(d.country_code)
          if (name) {
            setCountry(name)
            setDetected(true)
          }
        }
        if (d.region) setRegion(d.region)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const regions = useMemo(() => regionsFor(country), [country])
  const canContinue = !!country && (!requireRegion || regions.length === 0 || !!region)

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">{title ?? intl.formatMessage({ id: 'take.profile.title' })}</h3>
      {detected && (
        <p role="status" className="text-sm text-foreground-muted">
          <FormattedMessage id="take.profile.detected" />
        </p>
      )}
      <div>
        <label htmlFor="ps-country" className="block text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="take.profile.countryLabel" /></label>
        <select
          id="ps-country"
          value={country}
          onChange={(e) => { setCountry(e.target.value); setRegion('') }}
          className="w-full border border-border rounded-lg px-3 py-2"
        >
          <option value="">{intl.formatMessage({ id: 'take.profile.countryPlaceholder' })}</option>
          {COUNTRY_NAMES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {regions.length > 0 && (
        <div>
          <label htmlFor="ps-region" className="block text-sm font-medium text-foreground-muted mb-1"><FormattedMessage id="take.profile.regionLabel" /></label>
          <select
            id="ps-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full border border-border rounded-lg px-3 py-2"
          >
            <option value="">{intl.formatMessage({ id: 'take.profile.regionPlaceholder' })}</option>
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      )}
      <button
        type="button"
        disabled={!canContinue}
        onClick={() => onComplete({ country, region })}
        className="w-full bg-primary text-white font-semibold py-2.5 rounded-lg disabled:opacity-50"
      >
        <FormattedMessage id="take.profile.continue" />
      </button>
    </div>
  )
}

export default ProfileStep
